import { stableStringify } from '@provision/formatting-utils';
import {
  EscalationResponseType,
  EscalationRule,
  EscalationTimeConditionType,
} from '@provision/sidekick-utils';
import {
  BaseConversationUrgency,
  getUrgencyDisplay,
  UrgencyDisplayProps,
} from 'src/graphql/entities/conversation-urgency';
import { SignalUrgencyLevel } from 'src/graphql/entities/signal-business-hours/signal-business-hours.types';

export const ESCALATION_URGENCY_CONFIG = [
  {
    value: SignalUrgencyLevel.Normal,
    label: 'Normal',
    formLabel: 'Normal Escalation',
  },
  {
    value: SignalUrgencyLevel.Urgent,
    label: 'Urgent',
    formLabel: 'Urgent Escalation',
  },
  {
    value: SignalUrgencyLevel.LifeSafety,
    label: 'Life Safety',
    formLabel: 'Life Safety Escalation',
  },
] as const;

export const ESCALATION_URGENCY_OPTIONS = ESCALATION_URGENCY_CONFIG.map(
  ({ value, formLabel }) => ({
    value,
    label: formLabel,
  }),
);

export const ESCALATION_URGENCY_DISPLAY_LABELS: Record<string, string> = Object.fromEntries(
  ESCALATION_URGENCY_CONFIG.map(({ value, label }) => [value, label]),
);

// SignalUrgencyLevel uses `life-safety`, BaseConversationUrgency uses `life_safety`,
// so the display lookup is keyed off the conversation enum.
const URGENCY_DISPLAY_KEYS: Record<string, BaseConversationUrgency> = {
  [SignalUrgencyLevel.Normal]: BaseConversationUrgency.Normal,
  [SignalUrgencyLevel.Urgent]: BaseConversationUrgency.Urgent,
  [SignalUrgencyLevel.SignatureUrgent]: BaseConversationUrgency.Urgent,
  [SignalUrgencyLevel.LifeSafety]: BaseConversationUrgency.LifeSafety,
};

/** The icon and color an escalation policy shows in place of a severity glyph. */
export const getEscalationUrgencyDisplay = (
  urgencyLevel?: string | null,
): UrgencyDisplayProps =>
  getUrgencyDisplay(
    URGENCY_DISPLAY_KEYS[urgencyLevel ?? ''] ?? BaseConversationUrgency.Normal,
  );

export const ESCALATION_NOT_AVAILABLE_LABEL = 'Not available';

export const ESCALATION_RESPONSE_TYPE_OPTIONS: {
  value: EscalationResponseType;
  label: string;
}[] = [
  { value: 'fixed_duration', label: 'Within Duration' },
  { value: 'by_end_of_day', label: 'By End of Day' },
  { value: 'by_fixed_time', label: 'By Fixed Time' },
  { value: 'by_next_business_day', label: 'By Next Business Day' },
  { value: 'by_tomorrow', label: 'By Tomorrow' },
  { value: 'best_effort', label: 'Best Effort' },
];

export const ESCALATION_TIME_CONDITION_OPTIONS: {
  value: EscalationTimeConditionType;
  label: string;
}[] = [
  { value: 'always', label: 'Always' },
  { value: 'business_hours', label: 'Business Hours' },
  { value: 'outside_business_hours', label: 'Outside Business Hours' },
  { value: 'custom', label: 'Custom Schedule' },
];

export const ESCALATION_WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
] as const;

export function formatEnumLabel(value: string): string {
  return value?.replaceAll('_', ' ') ?? '';
}

export function serializeEscalationRules(rules: EscalationRule[]): string {
  return stableStringify({
    rules: [...rules]
      .sort((a, b) => a.order - b.order)
      .map((rule, index) => ({
        ...rule,
        order: index,
      })),
  });
}

export function formatServiceLevelLabel(serviceLevels: string[] | 'all'): string {
  if (serviceLevels === 'all' || serviceLevels.length === 0) {
    return 'All Service Levels';
  }

  if (serviceLevels.length === 1) {
    return serviceLevels[0];
  }

  return `${serviceLevels[0]} +${serviceLevels.length - 1} more`;
}
